import { useEffect, useRef, useState } from 'react';
import { useSplashState } from './SplashContext';
import { cssMobileShellHeaderPaddingTop } from '../shellIdentityStyles';
import { SPLASH_BG, SPLASH_FONT, SPLASH_WORDMARK_ATTR } from './splashTokens';
import { C, AUTH_GROUND_GRADIENT } from '../../components/auth/loginAuthTheme';
import { useSplashLeaveHandoff } from './useSplashLeaveHandoff';
import { useLogoJourneyActive } from './logoJourney';
import { useSignOutSplashActive } from './signOutSplash';
import './SplashProgress.css';
import { readSplashRoutePhaseMs, splashRoutePhaseDelay } from './splashRoutePhase';
import { ldLog } from '../../utils/ui/loginDebug';
import { isBootGateExemptPath } from '../../utils/ui/bootGateExemptPaths';

/**
 * The singleton CrewVex splash.
 *
 * Mounted once at the app root; never unmounted. Always renders the same
 * DOM element so the wordmark never remounts mid-session.
 *
 * Visibility is controlled purely via opacity + pointer-events, driven by
 * the aggregated signal from SplashContext:
 *   - any signal pushed  → fade in (after a short delay unless `immediate`)
 *   - all signals clear  → fade out
 *
 * Design rules (hard-learned):
 *   - Opacity-only motion. No transforms, no rise, no breath, no scramble —
 *     past attempts with those patterns felt glitchy on iOS.
 *   - The element is NEVER removed from the DOM (stable paint across signals).
 *   - Delay-based flash protection: deferred signals only paint if they
 *     outlast DELAY_MS, so transient recovery blips never flash the splash.
 *   - On initial boot the overlay is held visible until React's first effect
 *     cycle completes, so mount-time signals (BootGate) don't race with the
 *     overlay's own state and cause a spurious fade-out at startup.
 *   - Plain CrewVex (RD-SEC-021d / KAN-657): no Vex shine/shimmer — solid
 *     brand color, same as BrandLogo / the logo journey element.
 */

const DELAY_MS = 180;
/** Solid brand blue — matches theme `semantic.brand.primary` / BrandLogo. */
// theme: literal — splash shares the static preloader's colours; pre-paint theming is step 3
const VEX_COLOR = '#1c5aa5';

export default function SplashOverlay() {
  const { active, hasImmediate, veil } = useSplashState();

  // True until React's first post-commit effect tick. During that window,
  // visibility is forced on so we hand off seamlessly from the HTML
  // preloader without a racey fade-out/fade-in. Once this flips, normal
  // signal-driven visibility takes over.
  const [initialBoot, setInitialBoot] = useState(true);
  // KAN-1056: start the glide at its clock-based place in the loop (same as the preloader).
  const [routePhaseDelay] = useState(() => splashRoutePhaseDelay(readSplashRoutePhaseMs()));

  // `showing` is the gate for deferred (non-immediate) signals: it only
  // becomes true after the signal has been active for DELAY_MS, protecting
  // against transient blips.
  const [showing, setShowing] = useState(false);
  const delayTimerRef = useRef(null);

  useEffect(() => {
    // First effect tick: sibling effects (BootGate's useSplash, etc.) run
    // in the same batch. After they've all fired, React re-renders once
    // with the merged state. Flipping initialBoot here batches with any
    // signal registration, so the very next render either already has
    // active=true (splash stays visible) or active=false (splash fades).
    setInitialBoot(false);
  }, []);

  // Last painted visibility, read by the effect below without re-running it.
  const visibleRef = useRef(true);

  useEffect(() => {
    const clear = () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
        delayTimerRef.current = null;
      }
    };

    if (!active) {
      clear();
      setShowing(false);
      return undefined;
    }

    // HANDOFF (2026-09-25, founder: "CrewVex, blank, CrewVex again"): the
    // flash delay only protects an overlay that is HIDDEN. When an immediate
    // signal (boot / sign-in / sign-out) hands over to a deferred one
    // (ProtectedRoute, a route chunk), the overlay is already up — it used to
    // fade OUT and come back DELAY_MS later. Already visible → stay visible.
    if (hasImmediate || visibleRef.current) {
      clear();
      setShowing(true);
      return undefined;
    }

    // Deferred signal on a hidden overlay: wait DELAY_MS, then show.
    clear();
    delayTimerRef.current = setTimeout(() => {
      setShowing(true);
      delayTimerRef.current = null;
    }, DELAY_MS);
    return clear;
  }, [active, hasImmediate]);

  const wantVisible = initialBoot || hasImmediate || (active && showing);
  // KAN-562: on the way out, hand the wordmark to the screen below (glide) —
  // may keep the splash opaque a few frames for a target that is mounting.
  // Quiet sign-out veil (KAN-916; logo back in KAN-990): the sign-in ground + the logo, no rail. Quiet whenever the
  // sign-out cover is up, whatever other signals are still raised. Decided in the render that
  // turns the overlay visible (first visible frame) and latched for as long as it stays visible
  // (fade-out included), so it never flips to the brand look mid-fade.
  const signOutCover = useSignOutSplashActive();
  const veilRef = useRef(false);
  if (wantVisible) veilRef.current = (veilRef.current && visibleRef.current) || signOutCover || !!veil;
  const quiet = veilRef.current;
  const wordmarkRef = useRef(null);
  // KAN-990: the sign-out cover shows the logo too, and it glides into the login card's
  // logo like a cold start does, so the handoff is never told it is a no-glide veil.
  const { holdVisible, fadeTransition, wordmarkHidden, markStyle } = useSplashLeaveHandoff(wantVisible, wordmarkRef, false);
  // ONE logo on screen: while the logo-journey element carries the wordmark
  // (sign-in morph → rest → glide), the overlay's own copy stays hidden.
  const journeyActive = useLogoJourneyActive();

  const visible = wantVisible || holdVisible;
  visibleRef.current = visible;
  useEffect(() => { ldLog('splash', { visible, want: wantVisible, hold: holdVisible, journey: journeyActive }); }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // M2: the runner pauses only once the fade-out has FINISHED (opacity transition end, with a timer for
  // the no-transition paths), never at its start - pausing mid-fade froze the runner while still visible.
  const [cleared, setCleared] = useState(false);
  useEffect(() => {
    if (visible) { setCleared(false); return undefined; }
    const t = setTimeout(() => setCleared(true), 1500);
    return () => clearTimeout(t);
  }, [visible]);

  // KAN-1188: customer pages (/estimate, /unsubscribe) never get the CrewVex splash, not even the
  // first-frame hand-off from the HTML preloader (public/splash-phase.js hides that one too).
  if (typeof window !== 'undefined' && isBootGateExemptPath(window.location.pathname)) return null;

  return (
    <div
      aria-hidden={!visible}
      data-testid="splash-overlay"
      data-visible={visible ? 'true' : 'false'}
      data-cleared={!visible && cleared ? 'true' : 'false'}
      onTransitionEnd={(e) => { if (e.target === e.currentTarget && e.propertyName === 'opacity' && !visible) setCleared(true); }}
      data-quiet={quiet ? 'true' : 'false'}
      className="cvx-splash-root"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 100000,
        backgroundColor: quiet ? C.page : SPLASH_BG,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: `${cssMobileShellHeaderPaddingTop('16px')} 24px calc(env(safe-area-inset-bottom, 0px) + 16px)`,
        boxSizing: 'border-box',
        opacity: visible ? 1 : 0,
        transition: fadeTransition,
        pointerEvents: visible ? 'auto' : 'none',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      }}
    >
      {quiet && (
        <div aria-hidden data-testid="splash-veil-ground" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: AUTH_GROUND_GRADIENT }} />
      )}
      <span data-testid="splash-mark-slot" style={markStyle ? { ...splashMarkSlotStyle, ...markStyle } : splashMarkSlotStyle}>
        <span
          ref={wordmarkRef}
          className="notranslate"
          translate="no"
          style={{
            ...wordmarkStyle,
            // KAN-990: shown on the sign-out cover too (it glides into the login logo).
            visibility: wordmarkHidden || journeyActive ? 'hidden' : 'visible',
          }}
          {...{ [SPLASH_WORDMARK_ATTR]: '' }}
        >
          <span className="cvx-splash-crew" style={crewStyle}>Crew</span>
          <span className="cvx-splash-vex" style={vexStyle}>Vex</span>
        </span>
        {/* Nothing loads on sign-out, so its cover has no line (KAN-990). */}
        <span data-testid="splash-progress-rail" className="cvx-splash-progress-rail" aria-hidden="true" style={{ '--cvx-splash-phase': routePhaseDelay, visibility: quiet ? 'hidden' : 'visible' }}>
          <span className="cvx-splash-progress-track" />
          <span className="cvx-splash-progress-runner" />
        </span>
      </span>
    </div>
  );
}

const wordmarkStyle = {
  // 'Bricolage Splash' = the self-hosted, preloaded, font-display:optional face
  // (see public/index.html). Identical stack to the HTML preloader so the
  // preloader → React handoff never reflows the wordmark.
  fontFamily: SPLASH_FONT,
  fontWeight: 700,
  // Loading-screen wordmark stays SMALL (28–32px, founder 2026-10-10): the
  // sign-in wordmark is the big one (40 / 44px, KAN-758) — opening the app it
  // GROWS from here into the login, signing in it SHRINKS back to here.
  // Keep public/index.html's preloader in sync.
  fontSize: 'clamp(1.75rem, 4.5vw, 2rem)',
  letterSpacing: '-0.03em',
  lineHeight: 1.1,
  userSelect: 'none',
};

const splashMarkSlotStyle = {
  position: 'relative',
  display: 'inline-flex',
};

const crewStyle = {
  color: '#0f172a',
};

const vexStyle = {
  color: VEX_COLOR,
};
